import { xray } from 'lib/xray';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { getFeatureNormalizeConvertPdfPagesAmount } from 'reactApp/modules/features/features.selectors';
import { Ovidius } from 'reactApp/modules/ovidius/ovidius';
import { OvidiusPart } from 'reactApp/modules/ovidius/ovidius.types';
import { CloudFile, EStorageType } from 'reactApp/modules/storage/storage.types';
import { downloadAttach } from 'reactApp/utils/downloadDocumentHelpers';
import { mergePdfs } from 'reactApp/utils/mergePdfParts';

const MIN_ZOOM_SIZE = 0.25;
const MAX_ZOOM_SIZE = 4;
const ZOOM_STEP = 0.25;

export const print = async (content: ArrayBuffer[]) => {
    if (!content.length) {
        return;
    }

    const merged = await mergePdfs(content);
    const blob = new Blob([merged], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');

    iframe.src = url;
    iframe.style.display = 'none';

    const removeFrame = () => {
        iframe.remove();
        window.removeEventListener('focus', removeFrame);
    };

    window.addEventListener('focus', removeFrame);

    iframe.onload = function () {
        setTimeout(function () {
            if (iframe.contentWindow) {
                iframe.focus(); // Required for IE
                iframe.contentWindow.print();
            }
        }, 0);
    };

    document.body.appendChild(iframe);
};

export const useNormalizedOvidius = (params: {
    url: string;
    setContentForPrint: (content: ArrayBuffer[] | null) => void;
    storage?: EStorageType | null;
    file: CloudFile;
    attachType?: EAttachTypes;
}) => {
    const { url, storage, setContentForPrint, file, attachType } = params;

    const [parts, setParts] = useState<Array<OvidiusPart>>([]);
    const [isLoad, setIsLoad] = useState<boolean>(false);
    const [partsAmount, setPartsAmount] = useState<number>(0);
    const [content, setContent] = useState<ArrayBuffer[]>([]);
    const [scale, setScale] = useState(1);
    const [isError, setIsError] = useState(false);
    const [fullContent, setFullContent] = useState<ArrayBuffer | null>(null);

    const pdfPagesAmount = useSelector(getFeatureNormalizeConvertPdfPagesAmount);

    useEffect(() => {
        if (content.length === partsAmount && partsAmount > 0) {
            setContentForPrint(content);
        }
    }, [content, partsAmount]);

    useEffect(() => {
        /* Скачивание файла нужно только для аттачей */
        if (!attachType) {
            return;
        }

        const getFullContent = async () => {
            if (!storage) {
                setIsError(true);
                return;
            }

            const { content: data, error } = await downloadAttach(file);
            if (error || !data) {
                xray.send(`ovidius_v2_${attachType}_down_err`, {
                    rlog: 'cloud_norm_pdf_err',
                    rlog_message: {
                        message: 'ovidius_v2_download_attach_err',
                        error: error instanceof Error ? error : { error: JSON.stringify(error) },
                        attachType,
                    },
                });

                setIsError(true);
                return;
            }

            setFullContent(data);
        };

        getFullContent();

        return () => {
            setContent([]);
            setParts([]);
            setPartsAmount(0);
            setIsError(false);
            setIsLoad(false);
            setScale(1);
            setFullContent(null);
        };
    }, [url]);

    useEffect(() => {
        const fetchParts = async () => {
            if (!storage) {
                setIsError(true);
                return;
            }

            if (attachType && !fullContent) {
                return;
            }

            const { partsInfo, firstPart } = await Ovidius.getPdfFileParts(url, storage, file, fullContent, !!attachType);

            if (!partsInfo || !firstPart) {
                setIsError(true);
                return;
            }

            const { parts, parts_total: partsTotal, pages_total: pagesTotal } = partsInfo;

            if (pdfPagesAmount && pagesTotal > pdfPagesAmount) {
                setIsError(true);
                return;
            }

            setParts(parts);
            setContent([firstPart]);
            setIsLoad(true);
            setPartsAmount(partsTotal);

            if (parts.length === 1) {
                xray.send(`ovidius${attachType ? `_${attachType}` : ''}_one_part_success`);
            }
        };

        fetchParts();
    }, [fullContent]);

    useEffect(() => {
        const loadData = async () => {
            if (parts.length <= 1 || !storage) {
                return;
            }

            const getPdfFilePartInfo = Ovidius.getPdfFilePartCallback(url, storage, file, fullContent, !!attachType);

            for (let i = 1; i < parts.length; i++) {
                const { id } = parts[i];
                const { content: newPart } = await getPdfFilePartInfo(id);

                if (!newPart) {
                    // радар уже отправился в getPdfFilePartInfo
                    return;
                }

                setContent((oldContent) => [...oldContent, newPart]);
            }

            xray.send(`ovidius${attachType ? `_${attachType}` : ''}_getallparts_success`);
        };

        loadData();
    }, [parts]);

    const onIncreaseScaleClick = useCallback(() => {
        xray.send('norm_ovidius_zoom_plus');

        if (scale >= MAX_ZOOM_SIZE) {
            return;
        }

        setScale((old) => old + ZOOM_STEP);
    }, [scale]);

    const onDecreaseScaleClick = useCallback(() => {
        xray.send('norm_ovidius_zoom_minus');

        if (scale <= MIN_ZOOM_SIZE) {
            return;
        }

        setScale((old) => old - ZOOM_STEP);
    }, [scale]);

    return {
        content,
        isLoad,
        partsAmount,
        onIncreaseScaleClick,
        onDecreaseScaleClick,
        scale,
        isError,
    };
};
